import { useEffect, useState } from 'react';
import SideMenu from '../components/Menu';
import { useAuth } from '../context/AuthContext';
import ManuscriptDetailsModal from '../components/modals/ManuscriptDetailsModal';

export interface ParsedManuscript {
  manuscriptNumber: string;
  title: string;
  journal: string;
  status: 'Submitted' | 'In Review' | 'Revision Required' | 'Accepted' | 'Published' | 'Unknown';
  date: string;
  threadKey: string;
  parsedBy: 'gpt' | 'none';
  originalSubject?: string;
  from?: string;
  body?: string;
  summary?: string;
}

interface ManuscriptEmail {
  subject: string;
  from: string;
  date: string;
  snippet: string;
  body?: string;
}

interface ManuscriptThread {
  title: string;
  latestStatus: string;
  statusColor: string;
  lastUpdated: string;
  from: string;
  threadKey: string;
  history: ManuscriptEmail[];
  manualStatus?: ParsedManuscript['status'];
  manualColor?: 'green' | 'yellow' | 'red' | 'gray';
}

const Manuscripts = () => {
  const [manuscripts, setManuscripts] = useState<ManuscriptThread[]>([]);
  const [lastFetched, setLastFetched] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [refreshCooldown, setRefreshCooldown] = useState<number | null>(null);
  const [selectedManuscript, setSelectedManuscript] = useState<ManuscriptThread | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const isAuthenticated = useAuth()?.isAuthenticated;

  const fetchManuscripts = async (refresh = false) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/manuscripts${refresh ? '?refresh=true' : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 429) {
        const data = await response.json();
        setRefreshCooldown(data.retryAfterMs);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch manuscripts');
      }

      const data = await response.json();
      setManuscripts(data.manuscripts);
      setLastFetched(data.lastFetched);
    } catch (err) {
      console.error('Error fetching manuscripts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManuscripts();
  }, []);

  const statusStyle = {
    green: 'bg-green-200 text-green-800',
    yellow: 'bg-yellow-200 text-yellow-800',
    red: 'bg-red-200 text-red-800',
    gray: 'bg-gray-200 text-gray-800',
  };

  return (
    <>
      {isAuthenticated && <SideMenu />}
      <div
        id="manuscripts"
        className="p-3 h-screen bg-accent w-full"
        style={{ backgroundColor: '#5BC0BE' }}
      >
        <div className="bg-white rounded-lg h-full flex flex-col overflow-hidden">
          <div className="pt-6 px-4 sm:px-8 pb-2 flex-shrink-0">
            <h1
              className="text-3xl sm:text-4xl font-bold text-center mb-6"
              style={{ color: '#0B132B' }}
            >
              My Manuscripts
            </h1>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-500">
                Last fetched: {new Date(lastFetched).toLocaleString()}
              </p>
              <button
                onClick={() => fetchManuscripts(true)}
                className="bg-teal-500 text-white px-4 py-2 rounded hover:bg-teal-600"
                disabled={refreshCooldown !== null}
              >
                Refresh
              </button>
            </div>

            {refreshCooldown && (
              <div className="text-sm text-red-600 mb-3">
                Please wait {Math.ceil(refreshCooldown / 1000)}s before refreshing again.
              </div>
            )}

            <div className="flex justify-end mb-4">
              <div className="relative w-full sm:w-1/2 flex items-center">
                <input
                  type="text"
                  placeholder="Search by title or sender..."
                  className="border rounded-md px-3 py-2 text-sm w-full min-w-[160px]"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <div
                  className="loader animate-spin rounded-full h-12 w-12 border-t-4 border-b-4"
                  style={{ borderColor: '#5BC0BE' }}
                ></div>
              </div>
            ) : manuscripts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No manuscripts available.</div>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-full bg-white">
                  <thead className="sticky top-0 bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-xs border-b text-left">Title</th>
                      <th className="px-4 py-2 text-xs border-b text-left">From</th>
                      <th className="px-4 py-2 text-xs border-b text-left">Last Updated</th>
                      <th className="px-4 py-2 text-xs border-b text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manuscripts
                      .filter(
                        m =>
                          m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (m.from || '').toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map((m, i) => (
                        <tr
                          key={i}
                          onClick={() => setSelectedManuscript(m)}
                          className="cursor-pointer hover:bg-gray-50"
                        >
                          <td className="px-4 py-2 text-xs border-b">{m.title}</td>
                          <td className="px-4 py-2 text-xs border-b">{m.from || 'N/A'}</td>
                          <td className="px-4 py-2 text-xs border-b">
                            {new Date(m.lastUpdated).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2 text-xs border-b">
                            <span
                              className={`text-sm px-2 py-1 rounded ${
                                statusStyle[
                                  (m.manualColor || m.statusColor) as keyof typeof statusStyle
                                ] || ''
                              }`}
                            >
                              {m.manualStatus || m.latestStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedManuscript && (
        <ManuscriptDetailsModal
          isOpen={!!selectedManuscript}
          onClose={() => setSelectedManuscript(null)}
          manuscript={selectedManuscript}
        />
      )}
    </>
  );
};

export default Manuscripts;
